import LoadingState from '../../utils/loading-state';
import urls from '../urls';
import CONSTANTS from '../../constants';
import recordForm from './record-form/record-form-store';

const MIN_BIZGRAPH_SEARCH_LENGTH = CONSTANTS.EXCLUSIONS.MIN_BIZGRAPH_SEARCH_LENGTH;

function initialState() {
    return Object.assign(
        {
            editable: true,
            noLoader: false,
        },
        exclusionsInitialState()
    );
}

function exclusionsInitialState() {
    return {
        exclusionsLoadingState: new LoadingState({ok: true}),
        exclusionsContacts: [],
        duplicateContacts: [],
        exclusionsLastPromise: undefined,
        exclusionsSearchParams: {},
        exclusionsPercent: 0,
    }
}

export default {
    namespaced: true,
    state: initialState(),
    mutations: Object.assign(
        {
            setEditable(state, editable) {
                state.editable = editable;
            },
            setExclusionsLastPromise(state, exclusionsLastPromise) {
                state.exclusionsLastPromise = exclusionsLastPromise;
            },
            setExclusionsSearchParams(state, exclusionsSearchParams) {
                state.exclusionsSearchParams = exclusionsSearchParams;
            },
            setExclusionsContacts(state, exclusionsContacts) {
                state.exclusionsContacts = exclusionsContacts;
            },
            setDuplicateContacts(state, duplicateContacts) {
                state.duplicateContacts = duplicateContacts;
            },
            setExclusionsPercent(state, exclusionsPercent) {
                state.exclusionsPercent = exclusionsPercent;
            },
            resetExclusionsState(state) {
                Object.assign(state, exclusionsInitialState());
            },
            setNoLoader(state, noLoader) {
                state.noLoader = noLoader;
            },
        },
        LoadingState.getMutations('exclusionsLoadingState')
    ),
    modules: {
        recordForm,
    },
    actions: {
        init,
        processExclusions,
    },
};

function _getContactExclusionKeys(context) {
    const fields = context.rootGetters['worker/recordDetailSession/contactFields'];
    return fields.filter(field => field.exclusion).map(field => field.key);
}

function init(context, {noLoader} = {}) {
    _initFields(context);
    context.commit('setEditable', true);
    context.commit('setNoLoader', noLoader);
    _getContactExclusionKeys(context).forEach((searchKey) => {
        context.commit('recordForm/setFieldAttr', [searchKey, 'postpend', {
            type: 'text',
            disabled: false,
            value: 0,
        }]);
    });
}

function _initFields(context) {
    const contactFields = context.rootGetters['worker/recordDetailSession/contactFields'];
    // LG-12171 - limit to exclusion field
    const exclusionContactFields = contactFields.filter(field => field.exclusion);
    context.dispatch('recordForm/initFields', exclusionContactFields);
}

function _getExclusionFieldKeys(context) {
    const session = context.rootState.worker.recordDetailSession;
    return session.fields.filter(field => field.exclusion).map(field => field.key);
}

function _getExclusionsSearchParams(context) {
    const fields = context.state.recordForm.fields;
    const params = {};
    fields.filter(f => _getExclusionFieldKeys(context).includes(f.key)).forEach(f => {
        const value = f.value || '';
        params[f.key] = value.length >= MIN_BIZGRAPH_SEARCH_LENGTH ? value : undefined;
    });
    return params;
}

function processExclusions(context) {
    const currentParams = _getExclusionsSearchParams(context);
    const lastParams = context.state.exclusionsSearchParams;

    // if all params are empty then clear search results
    if (Object.values(currentParams).every(value => !value)) {
        context.commit('resetExclusionsState');
        return;
    }
    // if some params are different from last search then execute a new search
    const exclusionKeys = _getExclusionFieldKeys(context);
    if (exclusionKeys.some((key) => currentParams[key] !== lastParams[key])) {
        context.commit('setExclusionsLoadingStateLoading');
        _debouncedFetchExclusions(context, currentParams);
    }
}

function _fetchExclusions(context, params) {
    context.commit('setExclusionsSearchParams', params);
    const session = context.rootState.worker.recordDetailSession;
    const url = urls.contactExclusions(session.customer.id);
    const promise = Vue.http.get(url, {params: Object.assign({noLoader: true}, params)});
    context.commit('setExclusionsLastPromise', promise);
    return promise.then(
        response => {
            if (promise !== context.state.exclusionsLastPromise) return;
            const contacts = response.body.results;
            context.commit('setExclusionsContacts', contacts.filter(c => c.is_exclusion));
            context.commit('setDuplicateContacts', contacts.filter(c => !c.is_exclusion));
            _updateExclusionsPercentage(context);
            context.commit('setExclusionsLoadingStateOk');
        },
        response => {
            context.commit('setExclusionsLoadingStateError', _getErrorMessage(response));
        }
    );
}

const _debouncedFetchExclusions = debounce(_fetchExclusions, 300);

function _getErrorMessage(response) {
    let message = 'Unexpected error occurred. We are working on it.';
    try {
        if (response.body.detail) message = response.body.detail;
    } catch (e) {
        console.error(e);
    }
    return message;
}

function _updateExclusionsPercentage(context) {
    const percentages = _calculatePercentages(context)  ;
    percentages.forEach((fieldPercentage) => {
        context.commit('recordForm/setFieldAttr', [fieldPercentage.key, 'postpend', {
            type: 'text',
            disabled: false,
            value: fieldPercentage.value,
        }]);
    });
    // Find max value across all fields
    const exclusionsPercent = Math.max.apply(Math, percentages.map(item => item.value).concat(0));
    context.commit('setExclusionsPercent', exclusionsPercent);
}

function _calculatePercentages(context) {
    const contacts = context.state.exclusionsContacts.concat(context.state.duplicateContacts);
    const params = context.state.exclusionsSearchParams;
    let percentages = [];
    _getContactExclusionKeys(context).forEach((fieldKey) => {
        const paramValue = (params[fieldKey] || '').trim().toLowerCase();
        if (!paramValue.length) {
            percentages.push({key: fieldKey, value: 0});
            return;
        }
        const matchValues = contacts.map((contact) => {
            const value = (contact[fieldKey] || '').toLowerCase();
            if (!value.length) return 0;
            const nonMatching = value.replace(paramValue, '');
            return Math.round((value.length - nonMatching.length) * 100 / value.length);
        });
        percentages.push({
            key: fieldKey,
            value: Math.max.apply(Math, matchValues.concat(0))  // adding zero in case list is empty
        });
    });
    return percentages;
}
